import { Http } from "@Services";
import {
    IPresentationAdmin,
    IPresentationScreen,
    IPresentationState,
    IRemoteCredentials,
    IRemoteState,
    PresentationAction,
    RemoteAction,
} from "@Interfaces/Entities/Presentation";

interface ApiResponse<T> {
    success: boolean;
    data: T;
}

const remoteUrl = ({ code }: IRemoteCredentials) =>
    `/present/remote/${encodeURIComponent(code)}`;

// The PIN never goes in a URL: it travels from the link's fragment to this header.
const remoteHeaders = ({ pin }: IRemoteCredentials) => ({
    "X-Remote-Pin": pin,
});

export const PresentationService = {
    /** Present Mode dialog data; creates the presentation on first call (admins only). */
    getAdmin: async (channelId: string): Promise<IPresentationAdmin> => {
        const { data } = await Http.Request<ApiResponse<IPresentationAdmin>>({
            method: "GET",
            url: `/groups/${channelId}/presentation`,
        });
        return data;
    },

    /** Start, end or move the session from the admin's own screen tab. */
    sendAction: async (
        channelId: string,
        action: PresentationAction
    ): Promise<IPresentationState> => {
        const { data } = await Http.Request<ApiResponse<IPresentationState>>({
            method: "POST",
            url: `/groups/${channelId}/presentation/actions`,
            data: action,
        });
        return data;
    },

    /** What the big screen shows right now (public, display only). */
    getScreen: async (
        code: string,
        signal?: AbortSignal
    ): Promise<IPresentationScreen> => {
        const { data } = await Http.Request<ApiResponse<IPresentationScreen>>({
            method: "GET",
            url: `/present/screen/${encodeURIComponent(code)}`,
            signal,
        });
        return data;
    },

    /** Takes the phone remote for this device (409 `remote_taken` if another phone has it). */
    claimRemote: async (
        credentials: IRemoteCredentials
    ): Promise<IRemoteState> => {
        const { data } = await Http.Request<ApiResponse<IRemoteState>>({
            method: "POST",
            url: `${remoteUrl(credentials)}/claim`,
            headers: remoteHeaders(credentials),
        });
        return data;
    },

    /** Read-only state for a phone not holding the remote (e.g. after it ended the session). */
    previewRemote: async (
        credentials: IRemoteCredentials
    ): Promise<IRemoteState> => {
        const { data } = await Http.Request<ApiResponse<IRemoteState>>({
            method: "GET",
            url: `${remoteUrl(credentials)}/preview`,
            headers: remoteHeaders(credentials),
        });
        return data;
    },

    /** The phone's 2-second poll (read-only). */
    getRemote: async (
        credentials: IRemoteCredentials,
        signal?: AbortSignal
    ): Promise<IRemoteState> => {
        const { data } = await Http.Request<ApiResponse<IRemoteState>>({
            method: "GET",
            url: remoteUrl(credentials),
            headers: remoteHeaders(credentials),
            signal,
        });
        return data;
    },

    /** A tap on the phone remote. */
    sendRemoteAction: async (
        credentials: IRemoteCredentials,
        action: RemoteAction
    ): Promise<IRemoteState> => {
        const { data } = await Http.Request<ApiResponse<IRemoteState>>({
            method: "POST",
            url: `${remoteUrl(credentials)}/actions`,
            headers: remoteHeaders(credentials),
            data: action,
        });
        return data;
    },

    /** Admin's "Disconnect phone": frees the remote and gives the event a new PIN. */
    resetRemote: async (channelId: string): Promise<IPresentationAdmin> => {
        const { data } = await Http.Request<ApiResponse<IPresentationAdmin>>({
            method: "POST",
            url: `/groups/${channelId}/presentation/remote/reset`,
        });
        return data;
    },
};
